import React, { useState, useEffect } from "react";
import Select from "react-select";
import { Save } from "lucide-react";
import { AppCard, AppCardContent, AppCardHeader, AppCardTitle, AppButton, BackButton } from "../ui";
import {
  permissionCategories,
  TOTAL_PERMISSIONS,
  ALL_PERMISSION_KEYS,
  ROLE_OPTIONS,
  DEPARTMENT_OPTIONS,
} from "../../data/permissionCategories";
import { useBranch } from "../../contexts/BranchContext";
import "./admins.scss";

const initialState = {
  firstName: "",
  lastName: "",
  email: "",
  mobile: "",
  role: "Admin",
  department: "",
  employeeId: "",
  jobTitle: "",
  reportingManager: "",
  permissions: [],
  notes: "",
  status: "active",
};

const AdminForm = ({
  mode = "add",
  initialData,
  onSubmit,
  loading = false,
  onCancel,
  admins = [],
}) => {
  const [form, setForm] = useState(initialData || initialState);
  const { branches, isMultiBranch } = useBranch();

  useEffect(() => {
    if (initialData) setForm(initialData);
  }, [initialData]);

  const handleChange = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const togglePermission = (key) => {
    setForm((prev) => ({
      ...prev,
      permissions: prev.permissions.includes(key)
        ? prev.permissions.filter((p) => p !== key)
        : [...prev.permissions, key],
    }));
  };

  const toggleCategory = (category) => {
    const catPerms = category.permissions.map((p) => p.key);
    const allSelected = catPerms.every((k) => form.permissions.includes(k));
    if (allSelected) {
      setForm((prev) => ({
        ...prev,
        permissions: prev.permissions.filter((p) => !catPerms.includes(p)),
      }));
    } else {
      setForm((prev) => ({
        ...prev,
        permissions: [...new Set([...prev.permissions, ...catPerms])],
      }));
    }
  };

  const selectAll = () => setForm((prev) => ({ ...prev, permissions: [...ALL_PERMISSION_KEYS] }));
  const clearAll = () => setForm((prev) => ({ ...prev, permissions: [] }));

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit && onSubmit(form);
  };

  const isEdit = mode === "edit";

  // Managers list for select
  const managerOptions = admins.filter(
    (a) => (a.role === "Super Admin" || a.role === "Admin") && a._id !== initialData?._id
  );

  return (
    <form className="admin-form-page" onSubmit={handleSubmit}>
      {/* Header */}
      <div className="admin-form-header">
        <div className="admin-form-header__left">
          <BackButton onClick={onCancel} />
          <div>
            <h1 className="admin-form-header__title">
              {isEdit ? "Edit Admin" : "Add New Admin"}
            </h1>
            <p className="admin-form-header__subtitle">
              {isEdit
                ? `Editing ${form.firstName} ${form.lastName}`
                : "Register a new admin account with permissions"}
            </p>
          </div>
        </div>
        <div className="admin-form-header__actions">
          <AppButton variant="outline" type="button" onClick={onCancel}>
            Cancel
          </AppButton>
          <AppButton variant="primary" type="submit" disabled={loading}>
            <Save size={16} />
            {isEdit ? "Save Changes" : "Register Admin"}
          </AppButton>
        </div>
      </div>

      {/* Two-column layout */}
      <div className="admin-form-layout">
        {/* Main column */}
        <div className="admin-form-main">
          {/* Personal Information */}
          <AppCard className="admin-form-card admin-form-card--with-header">
            <AppCardHeader>
              <AppCardTitle>Personal Information</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="admin-form-grid">
                <div className="admin-form-field">
                  <label className="admin-form-label">First Name *</label>
                  <input
                    className="admin-form-input"
                    name="firstName"
                    value={form.firstName}
                    onChange={handleInputChange}
                    placeholder="Enter first name"
                    required
                  />
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Last Name *</label>
                  <input
                    className="admin-form-input"
                    name="lastName"
                    value={form.lastName}
                    onChange={handleInputChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Email Address *</label>
                  <input
                    className="admin-form-input"
                    type="email"
                    name="email"
                    value={form.email}
                    onChange={handleInputChange}
                    placeholder="admin@example.com"
                    required
                  />
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Mobile Number</label>
                  <input
                    className="admin-form-input"
                    name="mobile"
                    type="tel"
                    placeholder="9876543210"
                    pattern="[0-9]{10}"
                    maxLength={10}
                    minLength={10}
                    value={form.mobile}
                    onChange={handleInputChange}
                  />
                </div>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Organization Details */}
          <AppCard className="admin-form-card admin-form-card--with-header">
            <AppCardHeader>
              <AppCardTitle>Organization Details</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <div className="admin-form-grid">
                <div className="admin-form-field">
                  <label className="admin-form-label">Role *</label>
                  <select
                    className="admin-form-select"
                    value={form.role || "Admin"}
                    onChange={(e) => handleChange("role", e.target.value)}
                  >
                    {ROLE_OPTIONS.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Department *</label>
                  <select
                    className="admin-form-select"
                    value={form.department || ""}
                    onChange={(e) => handleChange("department", e.target.value)}
                    required
                  >
                    <option value="" disabled>Select department</option>
                    {DEPARTMENT_OPTIONS.map((d) => (
                      <option key={d} value={d}>{d}</option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Job Title *</label>
                  <input
                    className="admin-form-input"
                    name="jobTitle"
                    value={form.jobTitle}
                    onChange={handleInputChange}
                    placeholder="e.g. Operations Manager"
                    required
                  />
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Employee ID</label>
                  <input
                    className="admin-form-input"
                    name="employeeId"
                    value={form.employeeId}
                    onChange={handleInputChange}
                    placeholder="e.g. EMP006"
                  />
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Reporting Manager</label>
                  <select
                    className="admin-form-select"
                    value={form.reportingManager || ""}
                    onChange={(e) => handleChange("reportingManager", e.target.value)}
                  >
                    <option value="">None (Top Level)</option>
                    {managerOptions.map((a) => (
                      <option key={a._id} value={`${a.firstName} ${a.lastName}`}>
                        {a.firstName} {a.lastName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="admin-form-field">
                  <label className="admin-form-label">Status</label>
                  <div className="admin-form-switch">
                    <button
                      type="button"
                      className={`admin-form-switch__toggle ${form.status === "active" ? "admin-form-switch__toggle--active" : ""}`}
                      onClick={() => handleChange("status", form.status === "active" ? "inactive" : "active")}
                    />
                    <span className="admin-form-switch__label">
                      {form.status === "active" ? "Active" : "Inactive"}
                    </span>
                  </div>
                </div>
                {isMultiBranch && branches.length > 0 && (
                  <div className="admin-form-field admin-form-field--full">
                    <label className="admin-form-label">Branches</label>
                    <Select
                      isMulti
                      options={branches.filter(b => b.isActive).map(b => ({ value: b._id, label: b.name }))}
                      value={branches.filter(b => (form.branches || []).includes(b._id)).map(b => ({ value: b._id, label: b.name }))}
                      onChange={(selected) => handleChange("branches", (selected || []).map(s => s.value))}
                      placeholder="Assign to branches..."
                      classNamePrefix="bf-rs"
                      isClearable
                      styles={{
                        control: (base) => ({ ...base, backgroundColor: "var(--input-bg)", borderColor: "var(--border-primary)", borderRadius: "8px", minHeight: "38px", "&:hover": { borderColor: "var(--text-tertiary)" } }),
                        menu: (base) => ({ ...base, backgroundColor: "var(--card-bg)", border: "1px solid var(--border-primary)", borderRadius: "8px", boxShadow: "0 4px 12px var(--card-shadow)" }),
                        option: (base, state) => ({ ...base, backgroundColor: state.isSelected ? "#3b82f6" : state.isFocused ? "var(--bg-hover)" : "transparent", color: state.isSelected ? "#fff" : "var(--text-primary)" }),
                        singleValue: (base) => ({ ...base, color: "var(--text-primary)" }),
                        multiValue: (base) => ({ ...base, backgroundColor: "var(--bg-secondary)" }),
                        multiValueLabel: (base) => ({ ...base, color: "var(--text-primary)" }),
                        input: (base) => ({ ...base, color: "var(--text-primary)" }),
                      }}
                    />
                  </div>
                )}
              </div>
            </AppCardContent>
          </AppCard>

          {/* Permissions & Access */}
          <AppCard className="admin-form-card">
            <AppCardContent>
              <div className="admin-perms-header">
                <div className="admin-perms-header__left">
                  <h3 className="admin-perms-title">Permissions & Access</h3>
                  <span className="admin-perms-count">{form.permissions.length}/{TOTAL_PERMISSIONS}</span>
                </div>
                <div className="admin-perms-actions">
                  <AppButton variant="outline" size="sm" type="button" onClick={selectAll}>
                    Select All
                  </AppButton>
                  <AppButton variant="outline" size="sm" type="button" onClick={clearAll}>
                    Clear All
                  </AppButton>
                </div>
              </div>

              <div className="admin-perms-grid">
                {permissionCategories.map((category) => {
                  const CatIcon = category.icon;
                  const catPerms = category.permissions.map((p) => p.key);
                  const selectedCount = catPerms.filter((k) => form.permissions.includes(k)).length;
                  const allSelected = selectedCount === catPerms.length;

                  return (
                    <div key={category.category} className="admin-perm-category">
                      <div className="admin-perm-category__header">
                        <label className="admin-perm-checkbox">
                          <input
                            type="checkbox"
                            checked={allSelected}
                            onChange={() => toggleCategory(category)}
                          />
                        </label>
                        <div className="admin-perm-category__icon">
                          <CatIcon size={14} />
                        </div>
                        <span className="admin-perm-category__name">{category.category}</span>
                        <span className="admin-perm-category__count">{selectedCount}/{catPerms.length}</span>
                      </div>
                      <div className="admin-perm-category__items">
                        {category.permissions.map((perm) => (
                          <label key={perm.key} className="admin-perm-checkbox">
                            <input
                              type="checkbox"
                              checked={form.permissions.includes(perm.key)}
                              onChange={() => togglePermission(perm.key)}
                            />
                            <span>{perm.label}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </AppCardContent>
          </AppCard>

          {/* Additional Notes */}
          <AppCard className="admin-form-card admin-form-card--with-header">
            <AppCardHeader>
              <AppCardTitle>Additional Notes</AppCardTitle>
            </AppCardHeader>
            <AppCardContent>
              <textarea
                className="admin-form-textarea"
                name="notes"
                rows={4}
                placeholder="Add any notes about this admin account..."
                value={form.notes}
                onChange={handleInputChange}
              />
            </AppCardContent>
          </AppCard>
        </div>

        {/* Sidebar */}
        <div className="admin-form-sidebar">
          {/* Preview */}
          <AppCard className="admin-sidebar-card">
            <AppCardContent>
              <h3 className="admin-sidebar-title">Preview</h3>
              <div style={{ display: "flex", flexDirection: "column", gap: "0.5rem" }}>
                <div className="admin-sidebar-row">
                  <span className="admin-sidebar-row__label">Name</span>
                  <span className="admin-sidebar-row__value">
                    {form.firstName || form.lastName
                      ? `${form.firstName} ${form.lastName}`.trim()
                      : "—"}
                  </span>
                </div>
                <div className="admin-sidebar-row">
                  <span className="admin-sidebar-row__label">Role</span>
                  <span className="admin-sidebar-row__value">{form.role || "—"}</span>
                </div>
                <div className="admin-sidebar-row">
                  <span className="admin-sidebar-row__label">Department</span>
                  <span className="admin-sidebar-row__value">{form.department || "—"}</span>
                </div>
                <div className="admin-sidebar-row">
                  <span className="admin-sidebar-row__label">Status</span>
                  <span className="admin-sidebar-row__value" style={{ textTransform: "capitalize" }}>
                    {form.status}
                  </span>
                </div>
                <div className="admin-sidebar-row">
                  <span className="admin-sidebar-row__label">Permissions</span>
                  <span className="admin-sidebar-row__value">
                    {form.permissions.length}/{TOTAL_PERMISSIONS}
                  </span>
                </div>
              </div>
            </AppCardContent>
          </AppCard>

          {/* Permission Coverage */}
          <AppCard className="admin-sidebar-card">
            <AppCardContent>
              <h3 className="admin-sidebar-title">Permission Coverage</h3>
              <div className="admin-coverage-list">
                {permissionCategories.map((cat) => {
                  const granted = cat.permissions.filter((p) =>
                    form.permissions.includes(p.key)
                  ).length;
                  const total = cat.permissions.length;
                  const pct = Math.round((granted / total) * 100);
                  return (
                    <div key={cat.category} className="admin-coverage-item">
                      <div className="admin-coverage-item__header">
                        <span className="admin-coverage-item__name">{cat.category}</span>
                        <span className="admin-coverage-item__count">{granted}/{total}</span>
                      </div>
                      <div className="admin-coverage-bar">
                        <div
                          className="admin-coverage-bar__fill"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </AppCardContent>
          </AppCard>
        </div>
      </div>
    </form>
  );
};

export default AdminForm;
